import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, Ban, Check, Pencil, UserX, Zap } from 'lucide-react';
import { MISC_TASK_STATUS, MiscTaskRow, MiscTaskViewer } from '../../../interfaces/MiscTaskApprovalInterface';
import { approverNames, fullName, money } from './miscTaskHelpers';

/**
 * NPM Reds — the three dialogs: deny, force-approve, and the oversight
 * description correction.
 *
 * Portalled to <body> so no ancestor transform can trap the fixed overlay. That
 * puts them outside both .mtb and .appv2, which is why .mtb-modal-bg carries its
 * own token mapping and appears in the selector list in styles/v2/tokens.css.
 *
 * SiteContext's createPopup is NOT usable here: it is a blocking single-message
 * modal, not a form.
 */

export type MiscTaskDialogKind = 'deny' | 'force' | 'edit';

export interface MiscTaskDialogState {
  kind: MiscTaskDialogKind;
  row: MiscTaskRow;
  /** Carried into the force dialog so a typed-but-rejected draft isn't lost. */
  seedDescription?: string;
}

interface Props {
  state: MiscTaskDialogState;
  viewer: MiscTaskViewer | null;
  busy: boolean;
  onClose: () => void;
  onDeny: (row: MiscTaskRow, reason: string) => void;
  onForce: (row: MiscTaskRow, superDescription: string) => void;
  onEdit: (row: MiscTaskRow, superDescription: string) => void;
}

const MAX_DESCRIPTION = 60;

/** Counter tone: amber as the limit approaches, red at it. */
const countClass = (n: number): string => (n >= MAX_DESCRIPTION ? 'cnt full' : n >= 50 ? 'cnt near' : 'cnt');

const MiscTaskDialogs: React.FC<Props> = ({ state, viewer, busy, onClose, onDeny, onForce, onEdit }) => {
  const { kind, row } = state;
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const reasonRef = useRef<HTMLTextAreaElement>(null);
  const descriptionRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setReason('');
    setDescription(kind === 'edit' ? (row.superDescription ?? '') : (state.seedDescription ?? ''));
    // Focus lands on the one field that gates the primary action.
    window.setTimeout(() => (reasonRef.current ?? descriptionRef.current)?.focus(), 0);
  }, [kind, row.miscEmployeeTaskID, row.superDescription, state.seedDescription]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const employee = fullName(row.name);
  const amount = money(row.originalAmountClaimed ?? row.amountClaimed);
  const waiting = approverNames(row);

  const body = (() => {
    if (kind === 'deny') {
      return (
        <>
          <div className="mh no">
            <div className="mi"><Ban size={19} /></div>
            <div>
              <h3>Deny {employee}&apos;s red</h3>
              <div className="ms">{amount} · {row.project?.altProjectID ?? '—'} · terminal</div>
            </div>
          </div>
          <div className="mb">
            <div className="warn no">
              <AlertTriangle size={15} />
              <div>
                <b>{amount} comes off their pay.</b> The amount is zeroed, the red is excluded from the
                Vista export, and there is no un-deny — only {row.name.first} editing it, which starts a
                new approval chain.
              </div>
            </div>
            <label htmlFor="mtb-deny-reason">
              Reason <span className="opt">— required, stored on the red</span>
            </label>
            <textarea
              id="mtb-deny-reason"
              ref={reasonRef}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why this is not being paid"
              disabled={busy}
            />
          </div>
          <div className="mf">
            <button type="button" className="mtb-btn" onClick={onClose} disabled={busy}>Cancel</button>
            <button
              type="button"
              className="mtb-btn deny-solid"
              disabled={busy || reason.trim().length === 0}
              onClick={() => onDeny(row, reason.trim())}
            >
              <Ban size={15} />{busy ? 'Denying…' : 'Deny and zero the amount'}
            </button>
          </div>
        </>
      );
    }

    if (kind === 'force') {
      return (
        <>
          <div className="mh force">
            <div className="mi"><Zap size={19} /></div>
            <div>
              <h3>Force-approve</h3>
              <div className="ms">Recorded as FORCE_APPROVE — never as a normal approval</div>
            </div>
          </div>
          <div className="mb">
            <div className="warn force">
              <UserX size={15} />
              <div>
                {waiting ? (
                  <>
                    Approving <b>on behalf of {waiting}</b> and satisfying{' '}
                    {row.approvalStatus === MISC_TASK_STATUS.firstPending ? 'both approvals' : 'the 2nd approval'}{' '}
                    in one action.
                  </>
                ) : (
                  <><b>Nobody is assigned to this red</b> — forcing is the only way to clear it.</>
                )}
              </div>
            </div>
            <label htmlFor="mtb-force-desc">
              Vista description <span className="opt">— still required, 60 characters</span>
            </label>
            <input
              id="mtb-force-desc"
              type="text"
              maxLength={MAX_DESCRIPTION}
              ref={descriptionRef}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What is being paid for, in Vista's words"
              disabled={busy}
            />
            <div className={countClass(description.length)}>{description.length}/{MAX_DESCRIPTION}</div>
          </div>
          <div className="mf">
            <span className="fnote">Acting as <b>{viewer?.name ?? 'you'}</b> — from your session</span>
            <button type="button" className="mtb-btn" onClick={onClose} disabled={busy}>Cancel</button>
            <button
              type="button"
              className="mtb-btn force"
              disabled={busy || description.trim().length === 0}
              onClick={() => onForce(row, description.trim())}
            >
              <Zap size={15} />{busy ? 'Forcing…' : 'Force-approve'}
            </button>
          </div>
        </>
      );
    }

    return (
      <>
        <div className="mh plain">
          <div className="mi"><Pencil size={19} /></div>
          <div>
            <h3>Correct the Vista description</h3>
            <div className="ms">A correction — the status stays Approved</div>
          </div>
        </div>
        <div className="mb">
          <label htmlFor="mtb-edit-desc">
            Description <span className="opt">— 60 characters</span>
          </label>
          <input
            id="mtb-edit-desc"
            type="text"
            maxLength={MAX_DESCRIPTION}
            ref={descriptionRef}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={busy}
          />
          <div className={countClass(description.length)}>{description.length}/{MAX_DESCRIPTION}</div>
        </div>
        <div className="mf">
          <button type="button" className="mtb-btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button
            type="button"
            className="mtb-btn primary"
            disabled={busy || description.trim().length === 0}
            onClick={() => onEdit(row, description.trim())}
          >
            <Check size={15} />{busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </>
    );
  })();

  return createPortal(
    <div
      className="mtb-modal-bg"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
    >
      <div className="mtb-modal" role="dialog" aria-modal="true">{body}</div>
    </div>,
    document.body
  );
};

export default MiscTaskDialogs;
